// Table column configurations for different monitor tables.
// `labelKey` is an i18n key (see src/i18n/locales/*/translation.json). TableComponent
// renders it with t(labelKey); `key` still selects the row value.
import { DateTime } from "luxon"

export const pickerGroupsColumns = [
  { key: 'deviceGroupName', labelKey: 'columns.groupName' },
  { key: 'blockName', labelKey: 'columns.blockName' },
  { key: 'totalUnits', labelKey: 'columns.totalUnits' },
  { key: 'weight', labelKey: 'columns.weight' },
  { key: 'totalWorkHours', labelKey: 'columns.totalWorkHours' },
  { key: 'activeWorkers', labelKey: 'columns.activeWorkers' },
  { key: 'averagePerWorker', labelKey: 'columns.averagePerWorker' },
  { key: 'averageHourlyRatePerWorker', labelKey: 'columns.averageHourlyRatePerWorker' }
]

export const pickerMonitorColumns = [
  // Workers are still identified by their legacy id (old_id); fall back to the
  // internal worker_id only when a worker has no legacy id.
  { key: 'workerId', labelKey: 'columns.workerId', cell: (value, row) => row.oldId || row.workerId },
  { key: 'workerName', labelKey: 'columns.workerName' },
  { key: 'blockName', labelKey: 'columns.blockName' },
  { key: 'deviceGroupName', labelKey: 'columns.groupName' },
  { key: 'totalUnits', labelKey: 'columns.totalUnits' },
  { key: 'totalWorkHours', labelKey: 'columns.totalWorkHours' },
  { key: 'averageRatePerHour', labelKey: 'columns.averageRatePerHour' },
  { key: 'averageRatePerDay', labelKey: 'columns.averageRatePerDay' },
  { key: 'workerComments', labelKey: 'columns.workerComments' }
]

export const blockMonitorColumns = [
  { key: 'blockName', labelKey: 'columns.blockName' },
  { key: 'crop', labelKey: 'columns.crop' },
  { key: 'variety', labelKey: 'columns.variety' },
  { key: 'totalScans', labelKey: 'columns.totalScans' },
  { key: 'totalUnits', labelKey: 'columns.totalUnits' },
  { key: 'weight', labelKey: 'columns.weight' },
  { key: 'kgPerPlant', labelKey: 'columns.kgPerPlant' },
  { key: 'kgPerSquareMeter', labelKey: 'columns.kgPerSquareMeter' }
]

export const agroGroupsMonitoringColumns = [
  { key: 'groupName', labelKey: 'columns.groupName' },
  { key: 'blockName', labelKey: 'columns.blockName' },
  { key: 'totalUnits', labelKey: 'columns.totalUnits' },
  { key: 'totalWorkHours', labelKey: 'columns.totalWorkHours' },
  { key: 'activeWorkers', labelKey: 'columns.activeWorkers' },
  { key: 'averagePerWorker', labelKey: 'columns.averagePerWorker' },
  { key: 'averageHourlyRatePerWorker', labelKey: 'columns.averageHourlyRatePerWorker' }
]

export const agroActionsColumns = [
  { key: 'blockName', labelKey: 'columns.blockName' },
  { key: 'activityName', labelKey: 'columns.activityName' },
  { key: 'units', labelKey: 'columns.units' },
  {
    key: 'percentageCompletion',
    labelKey: 'columns.percentageOfCompletion',
    cell: (value) => {
      // Don't render the badge for empty values (like in totals row)
      if (!value || value === '') {
        return <span>{value}</span>
      }

      return (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          parseInt(value) === 100 ? 'bg-green-100 text-green-800' : parseInt(value) >= 50 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
        }`}
        >
          {value}
        </span>
      )
    }
  }
]

export const packerGroupsColumns = [
  { key: 'groupName', labelKey: 'columns.groupName' },
  { key: 'totalPacked', labelKey: 'columns.totalPacked' },
  { key: 'workerCount', labelKey: 'columns.workerCount' },
  { key: 'avgWeight', labelKey: 'columns.avgWeight' },
  { key: 'percentageOfTotal', labelKey: 'columns.percentageOfTotal' },
  { key: 'avgPerWorker', labelKey: 'columns.avgPerWorker' }
]

export const packerMonitorColumns = [
  // Workers are still identified by their legacy id (old_id); fall back to the
  // internal worker_id only when a worker has no legacy id.
  { key: 'workerId', labelKey: 'columns.workerId', cell: (value, row) => row.oldId || row.workerId },
  { key: 'workerName', labelKey: 'columns.workerName' },
  { key: 'groupName', labelKey: 'columns.groupName' },
  { key: 'containerNames', labelKey: 'columns.boxType' },
  { key: 'totalPacked', labelKey: 'columns.totalPacked' },
  { key: 'percentageOfTotal', labelKey: 'columns.percentageOfTotal' },
  { key: 'firstScan', labelKey: 'columns.firstScan' },
  { key: 'lastScan', labelKey: 'columns.lastScan' },
  { key: 'totalWorkHours', labelKey: 'columns.totalWorkHours' },
  { key: 'averageRatePerHour', labelKey: 'columns.averageRatePerHour' },
  { key: 'averageRatePerDay', labelKey: 'columns.averageRatePerDay' }
]

export const pickingColumns = [
  { key: 'blockName', labelKey: 'columns.blockName' },
  { key: 'containerName', labelKey: 'columns.containerName' },
  { key: 'totalCredits', labelKey: 'columns.totalCredits' },
  { key: 'totalContainers', labelKey: 'columns.totalContainers' }
]

export const pickingColumnsWithDate = [
  { key: 'blockName', labelKey: 'columns.blockName' },
  { key: 'containerName', labelKey: 'columns.containerName' },
  { key: 'totalCredits', labelKey: 'columns.totalCredits' },
  { key: 'totalContainers', labelKey: 'columns.totalContainers' },
  { key: 'date', labelKey: 'columns.date', cell: (value) => {
    return value ? DateTime.fromISO(value).setZone('America/Mexico_City').toJSDate().toLocaleDateString('es-MX') : ''
  } }
]

export const packingColumns = [
  { key: 'containerName', labelKey: 'columns.containerName' },
  { key: 'stickerType', labelKey: 'columns.stickerType' },
  { key: 'totalPacked', labelKey: 'columns.totalPacked' },
  { key: 'packerCount', labelKey: 'columns.packerCount' }
]

export const packingColumnsWithDate = [
  { key: 'containerName', labelKey: 'columns.containerName' },
  { key: 'stickerType', labelKey: 'columns.stickerType' },
  { key: 'totalPacked', labelKey: 'columns.totalPacked' },
  { key: 'packerCount', labelKey: 'columns.packerCount' },
  { key: 'date', labelKey: 'columns.date', cell: (value) => {
    return value ? DateTime.fromISO(value).setZone('America/Mexico_City').toJSDate().toLocaleDateString('es-MX') : ''
  } }
]
